import { ReactNode, useEffect } from 'react';
import { attachChatTicketOrder } from '../../../api/conversations.api';
import { ChatChoiceChevronIcon } from '../../../assets/svg';
import { Button } from '../../../components/button/button';
import {
  ChatTicket,
  SupportTicketStatus,
} from '../../../types/supportTicket.types';
import {
  awaitsOrderChoice,
  orderCandidateLines,
  ticketStatusLabel,
  ticketTypeLabel,
} from '../helpers/chatTriage';
import { useChatCardAction } from '../hooks/useChatCardAction';
import {
  CardButtons,
  CardChip,
  CardFooter,
  CardKicker,
  CardLink,
  CardPrompt,
  CardStatusRow,
  CardSurface,
  CardTitle,
  CardTone,
  ChoiceDetail,
  ChoiceLine,
  ChoiceList,
  ChoiceRow,
  ChoiceText,
  ChoiceTitle,
} from './MessageCards.styled';

const STATUS_TONE: Record<SupportTicketStatus, CardTone> = {
  open: 'wait',
  claimed: 'info',
  resolved: 'good',
};

type Props = {
  ticket: ChatTicket;
  /** Merchant side: the only one that may pick the order. */
  interactive: boolean;
  /** Admin only: opens the ticket in the support tickets list. */
  onOpenTicket?: (ticketId: number) => void;
  /** Opens the attached order. Absent on the merchant app, which has no such screen. */
  onOpenOrder?: (orderId: number) => void;
  /** Time and ticks, when this card is the whole message. */
  meta?: ReactNode;
};

/**
 * The request the bot handed to the support team.
 *
 * The merchant sees where it stands and, while the bot is still unsure which
 * order they mean, picks one. The card MOVES when the bot offers a new list:
 * the old message arrives again with `ticket: null` and loses its card, so this
 * only ever draws where the ticket actually is.
 */
export const TicketCard = ({
  ticket,
  interactive,
  onOpenTicket,
  onOpenOrder,
  meta,
}: Props) => {
  const { busy, run, settle } = useChatCardAction();

  /* An order was picked - here, or on the merchant's other device. */
  useEffect(() => {
    if (ticket.orderId !== null) settle();
  }, [ticket.orderId, settle]);

  const choosing = awaitsOrderChoice(ticket);
  const single = ticket.orderCandidates.length === 1;
  const attached = ticket.orderId !== null;
  const showOpenTicket = !interactive && !!onOpenTicket;

  /*
   * Which line ends the card, so the timestamp can ride beside it instead of
   * sitting alone underneath - a lone time leaves a band of empty card that
   * reads as something missing. The picker is the exception: it has content of
   * its own to sit under.
   */
  const tail = showOpenTicket
    ? 'link'
    : choosing
    ? 'picker'
    : attached
    ? 'attached'
    : 'chip';

  const pick = (orderId: number) =>
    run(`order:${orderId}`, () => attachChatTicketOrder(ticket.id, orderId));

  return (
    <CardSurface>
      <CardKicker>
        {ticketTypeLabel(ticket.type)} · #{ticket.id}
      </CardKicker>

      <CardTitle>{ticket.subject}</CardTitle>

      <CardStatusRow>
        <CardChip $tone={STATUS_TONE[ticket.status]}>
          {ticketStatusLabel(ticket.status, interactive ? 'merchant' : 'admin')}
        </CardChip>
        {tail === 'chip' && meta}
      </CardStatusRow>

      {attached && (
        <CardStatusRow>
          {onOpenOrder ? (
            <CardLink onClick={() => onOpenOrder(Number(ticket.orderId))}>
              Order #{ticket.orderId} attached
            </CardLink>
          ) : (
            <CardPrompt>Order #{ticket.orderId} attached</CardPrompt>
          )}
          {tail === 'attached' && meta}
        </CardStatusRow>
      )}

      {choosing && interactive && (
        <>
          <CardPrompt>
            {single ? 'Is this the order?' : 'Which order is it?'}
          </CardPrompt>

          {/* One candidate is a yes/no question, so the row states the order
              and a button answers it. Several are a choice, and there the rows
              ARE the buttons - a list of orders each needing a second tap on
              its own Confirm reads as five questions rather than one. */}
          <ChoiceList onPointerDown={(event) => event.stopPropagation()}>
            {ticket.orderCandidates.map((candidate) => {
              const { name, detail } = orderCandidateLines(candidate);
              const lines = (
                <ChoiceText>
                  <ChoiceTitle>{name}</ChoiceTitle>
                  {detail && <ChoiceDetail>{detail}</ChoiceDetail>}
                </ChoiceText>
              );

              return single ? (
                <ChoiceLine key={candidate.orderId}>{lines}</ChoiceLine>
              ) : (
                <ChoiceRow
                  key={candidate.orderId}
                  type="button"
                  $picked={busy === `order:${candidate.orderId}`}
                  disabled={!!busy}
                  onClick={() => pick(candidate.orderId)}
                >
                  {lines}
                  <ChatChoiceChevronIcon />
                </ChoiceRow>
              );
            })}
          </ChoiceList>

          {single && (
            <CardButtons onPointerDown={(event) => event.stopPropagation()}>
              <Button
                $selected
                $small
                $loading={!!busy}
                data-loading={busy ? 'true' : undefined}
                disabled={!!busy}
                onClick={() => pick(ticket.orderCandidates[0].orderId)}
              >
                Confirm
              </Button>
            </CardButtons>
          )}
        </>
      )}

      {choosing && !interactive && (
        <CardPrompt>Waiting for the merchant to pick an order</CardPrompt>
      )}

      {showOpenTicket && (
        <CardStatusRow>
          <CardLink onClick={() => onOpenTicket?.(ticket.id)}>
            Open ticket
          </CardLink>
          {meta}
        </CardStatusRow>
      )}

      {tail === 'picker' && meta && <CardFooter>{meta}</CardFooter>}
    </CardSurface>
  );
};
